import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ApiError } from '../../../../core/errors/api-error';
import { MessageService } from '../../../../core/services/message/message.service';
import {
  ITagListItem,
  ITagListResponse
} from '../../../environment/tags/interfaces/tag-list-response';
import { ITagRequest } from '../../../environment/tags/interfaces/tag-request.interface';
import { EnumSituacao } from '../../../shared/enums/situacao.enum';
import { TagApi } from '../../apis/tag.api';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { IMultiSelectSettings } from '../multi-select/models/multi-select-settings.interface';
import { IOption } from '../multi-select/models/option.interface';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'sgt-tags-multi-select',
  templateUrl: './tags-multi-select.component.html',
  styleUrls: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagsMultiSelectComponent),
      multi: true
    }
  ]
})
export class TagsMultiSelectComponent extends CustomControlValueAccessor<
  number[]
> {
  @Input() cadastros: number;
  @Input() situacoes = EnumSituacao.ATIVO;
  @Input() variante: 'filtro' | undefined;
  @Input() icon = 'fa-bookmark';
  @Input() settings: IMultiSelectSettings = {
    placeholder: 'Selecione as tags',
    singular: 'Tag',
    plural: 'Tags',
    searchEmptyResultMessage: 'Nenhuma tag encontrada'
  };

  @Output() changed = new EventEmitter<number[]>();

  tags: IOption[] = [];

  constructor(
    private messageService: MessageService,
    private tagApi: TagApi
  ) {
    super();
  }

  firstOpened(isFirstOpened: boolean): void {
    if (isFirstOpened && this.tags.length === 0 && this.cadastros) {
      this.fetchTags();
    }
  }

  private fetchTags(): void {
    const filter: ITagRequest = {
      cadastros: this.cadastros ? [this.cadastros] : undefined,
      situacoes: this.situacoes,
    };
    const currentIds = this.value ?? [];

    this.definirCarregando(true);

    this.tagApi.fetchList(filter)
      .pipe(finalize(() => this.definirCarregando(false)))
      .subscribe({
        next: (response: ITagListResponse) => {
          this.tags = response.data.map((type: ITagListItem) => ({
            id: type.id,
            label: type.nome,
            isChecked: currentIds.includes(type.id)
          }));
        },
        error: (error: ApiError) => {
          this.messageService.error('Houve um problema ao buscar as tags');
          console.error('Erro ao buscar tags:', error);
        }
      });
  }

  setSelectedTags(ids: number[]): void {
    const safeIds = ids ?? [];
    this._value = safeIds;
    this.tags.forEach((tag) => {
      tag.isChecked = safeIds.includes(tag.id);
    });
    if (safeIds.length > 0 && this.tags.length === 0 && this.cadastros) {
      this.fetchTags();
    }
  }

  clearSelection(): void {
    this._value = [];
    this.tags.forEach((tag) => {
      tag.isChecked = false;
    });
  }

  onSearch(): void {
    this.changed.emit(this.value);
  }

  onSelectionChange(): void {
    this.emitChange(this.value);
  }

  protected updateValue(value: number[] | null): void {
    const ids = value ?? [];

    this.tags.forEach((tag) => {
      tag.isChecked = ids.includes(tag.id);
    });

    if (ids.length > 0 && this.tags.length === 0 && this.cadastros) {
      this.fetchTags();
    }
    this.emitChange(ids);
  }

  protected onDisabledStateChange(isDisabled: boolean): void {
    this.settings = { ...this.settings, isDisabled };
  }

  private definirCarregando(carregando: boolean): void {
    this.settings = { ...this.settings, isLoading: carregando };
  }

}
